<div class="event-list-container">
    <div class="list-header">
        <h3 class="section-title">
            <mat-icon>list_alt</mat-icon>
            Event Records
            @if (totalCount() > 0) {
            <span class="count-badge">{{ totalCount() }}</span>
            }
        </h3>
    </div>

    @if (isLoading()) {
    <mat-progress-bar mode="indeterminate"></mat-progress-bar>
    }

    @if (!isLoading() && records().length === 0 && fetchId) {
    <div class="empty-state">
        <mat-icon>inbox</mat-icon>
        <p>No records found for this fetch.</p>
    </div>
    }

    @if (records().length > 0) {
    <div class="table-wrapper">
        <table mat-table [dataSource]="records()" multiTemplateDataRows class="events-table">

            <!-- Plate Number -->
            <ng-container matColumnDef="plateNumber">
                <th mat-header-cell *matHeaderCellDef>Plate</th>
                <td mat-cell *matCellDef="let row">
                    <span class="plate">{{ row.plateNumber || '—' }}</span>
                </td>
            </ng-container>

            <!-- Event Type -->
            <ng-container matColumnDef="eventType">
                <th mat-header-cell *matHeaderCellDef>Type</th>
                <td mat-cell *matCellDef="let row">
                    <mat-chip [color]="getEventTypeColor(row.eventType)" highlighted>
                        <mat-icon matChipAvatar>{{ getEventTypeIcon(row.eventType) }}</mat-icon>
                        {{ row.eventType || row.eventCode }}
                    </mat-chip>
                </td>
            </ng-container>

            <!-- Direction -->
            <ng-container matColumnDef="direction">
                <th mat-header-cell *matHeaderCellDef>Direction</th>
                <td mat-cell *matCellDef="let row">
                    @if (row.direction) {
                    <span class="direction-cell" [matTooltip]="row.direction">
                        <mat-icon>{{ getDirectionIcon(row.direction) }}</mat-icon>
                        {{ row.direction }}
                    </span>
                    } @else { <span>—</span> }
                </td>
            </ng-container>

            <!-- Event Time -->
            <ng-container matColumnDef="eventTime">
                <th mat-header-cell *matHeaderCellDef>Event Time</th>
                <td mat-cell *matCellDef="let row">{{ row.eventTime | date:'short' }}</td>
            </ng-container>

            <!-- Country -->
            <ng-container matColumnDef="country">
                <th mat-header-cell *matHeaderCellDef>Country</th>
                <td mat-cell *matCellDef="let row">{{ row.country || '—' }}</td>
            </ng-container>

            <!-- Plate Color -->
            <ng-container matColumnDef="plateColor">
                <th mat-header-cell *matHeaderCellDef>Plate Color</th>
                <td mat-cell *matCellDef="let row">{{ row.plateColor || '—' }}</td>
            </ng-container>

            <!-- Vehicle Color -->
            <ng-container matColumnDef="vehicleColor">
                <th mat-header-cell *matHeaderCellDef>Vehicle Color</th>
                <td mat-cell *matCellDef="let row">{{ row.vehicleColor || '—' }}</td>
            </ng-container>

            <!-- Category -->
            <ng-container matColumnDef="category">
                <th mat-header-cell *matHeaderCellDef>Category</th>
                <td mat-cell *matCellDef="let row">{{ row.category || '—' }}</td>
            </ng-container>

            <!-- Location -->
            <ng-container matColumnDef="location">
                <th mat-header-cell *matHeaderCellDef>Location</th>
                <td mat-cell *matCellDef="let row">
                    @if (row.detailedAddress) {
                    <span [matTooltip]="row.detailedAddress">{{ row.location || row.detailedAddress }}</span>
                    } @else {
                    {{ row.location || '—' }}
                    }
                </td>
            </ng-container>

            <!-- Details expand toggle -->
            <ng-container matColumnDef="details">
                <th mat-header-cell *matHeaderCellDef></th>
                <td mat-cell *matCellDef="let row">
                    <button mat-icon-button (click)="toggleExpand(row); $event.stopPropagation()"
                        [matTooltip]="expandedRow === row ? 'Collapse' : 'More details'">
                        <mat-icon>{{ expandedRow === row ? 'expand_less' : 'expand_more' }}</mat-icon>
                    </button>
                </td>
            </ng-container>

            <!-- Expanded detail row -->
            <ng-container matColumnDef="expandedDetail">
                <td mat-cell *matCellDef="let row" [attr.colspan]="displayedColumns.length">
                    @if (expandedRow === row) {
                    <div class="detail-row">
                        <div class="detail-grid">
                            <div class="detail-item">
                                <span class="detail-label">Rec #</span>
                                <span>{{ row.recNo ?? '—' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Vehicle Sign</span>
                                <span>{{ row.vehicleSign || '—' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Vehicle Direction</span>
                                <span>{{ row.vehicleDirection || '—' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Plate Size</span>
                                <span>{{ row.plateSize || '—' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Junction Direction</span>
                                <span>{{ row.junctionDirection ?? '—' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Snap Source</span>
                                <span>{{ row.snapSource || '—' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Master of Car</span>
                                <span>{{ row.masterOfCar || '—' }}</span>
                            </div>
                            <div class="detail-item">
                                <span class="detail-label">Detailed Address</span>
                                <span>{{ row.detailedAddress || '—' }}</span>
                            </div>
                        </div>
                    </div>
                    }
                </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;" [class.expanded-row]="expandedRow === row"
                (click)="toggleExpand(row)"></tr>
            <tr mat-row *matRowDef="let row; columns: ['expandedDetail']" class="detail-row-container"></tr>
        </table>
    </div>

    <mat-paginator [length]="totalCount()" [pageIndex]="pageIndex()" [pageSize]="pageSize()"
        [pageSizeOptions]="[10, 25, 50, 100]" (page)="onPageChange($event)" showFirstLastButtons>
    </mat-paginator>
    }
</div>